import React from "react";
import {
    Popper,
    Autocomplete,
    Box,
    Chip,
    Collapse,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    DeleteOutline,
    DragIndicator,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { DragDropContext, Draggable, type DropResult } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../../molecules/StrictModeDroppable";
import {
    type Question,
    type SpanWorkflowQuestionScope,
    type WorkflowTriggerPhase,
    QuestionType,
} from "../../../../types/rubric";
import type { AnswerValue } from "../../../../types/answerSet";
import ExpressionBuilder from "./ExpressionBuilder";
import {
    SPAN_ANSWERED_ID,
    SPAN_NOT_ANSWERED_ID,
} from "./workflowTriggerCapabilities";
import {
    getAnswersIncludingSelected,
    getTriggerPhase,
    isShowWhenTrigger,
    isSpanQuestionType,
    questionCardPalette,
    type CrossWorkflowSourceExtension,
    type QuestionsSectionProps,
    type WorkflowQuestionWithClientId,
} from "./triggerSectionShared";
import TriggerSection from "./TriggerSection";

export type { QuestionsSectionProps, WorkflowQuestionWithClientId, CrossWorkflowSourceExtension } from "./triggerSectionShared";

const spanScopeOptions: { value: SpanWorkflowQuestionScope; label: string }[] = [
    { value: "ALL_SPANS", label: "All spans" },
    { value: "SELECTED_BY_SPANS_QUESTION", label: "Spans selected by..." },
];

/**
 * Small wrapper around MUI Autocomplete that owns its own inputValue
 * state so we can force the input back to empty after each selection.
 *
 * Why a sub-component: the parent Autocomplete's `clearOnBlur` only
 * clears when the typed value doesn't match a valid option — but after
 * a selection the input value DOES match the selected option's label,
 * so `clearOnBlur` is a no-op. Controlled `inputValue` lets us reset
 * explicitly in `onChange` (for the user-selection case) and treat
 * MUI's reason='reset' input-change event (fired by the library on
 * select) as "force empty" rather than "set to selected label".
 */
const AddQuestionAutocomplete: React.FC<{
    options: Question[];
    onAddQuestion: (questionId: number) => void;
}> = ({ options, onAddQuestion }) => {
    // We force-remount the Autocomplete after each successful add by bumping
    // the `addCounter` and using it as a React key. This is more reliable than
    // controlling `inputValue` and trying to wrestle MUI's internal reset
    // handling — under real mouse-click sequencing the controlled value can
    // be re-asserted to the just-selected option's label by MUI's internal
    // reconciliation, leaving the input visibly populated until blur. Remount
    // discards all of that state in a single render.
    const [addCounter, setAddCounter] = React.useState(0);
    return (
        <Autocomplete<Question, false, false, false>
            key={`add-question-${addCounter}`}
            fullWidth
            blurOnSelect
            // Render the popper at the anchor's CURRENT measured client
            // width and force the inner Paper + listbox to fill that
            // popper. Without the inner overrides, the listbox inside the
            // popper renders at the longest option's content width and
            // leaves visible empty space on the right of the dropdown.
            // 100% on the inner slots is now safe because the popper has
            // an explicit pixel width (no body-portal sizing).
            PopperComponent={(popperProps) => {
                const anchorWidth =
                    popperProps.anchorEl instanceof HTMLElement
                        ? popperProps.anchorEl.clientWidth
                        : undefined;
                return (
                    <Popper
                        {...popperProps}
                        style={{
                            ...popperProps.style,
                            width: anchorWidth ? `${anchorWidth}px` : popperProps.style?.width,
                        }}
                    />
                );
            }}
            slotProps={{
                paper: { sx: { width: "100%" } },
            }}
            // ListboxProps is the right knob for the inner <ul>; the
            // `listbox` key under slotProps is silently ignored by MUI v5's
            // Autocomplete (its supported slots are popper / paper /
            // popupIndicator / clearIndicator / root only). Without this,
            // the listbox sized itself to the longest option's text width
            // and rendered visibly narrower than the popper Paper.
            ListboxProps={{
                style: { width: "100%", boxSizing: "border-box" },
            }}
            options={options}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={null}
            onChange={(_, newValue) => {
                if (newValue) {
                    onAddQuestion(newValue.id);
                    setAddCounter((n) => n + 1);
                }
            }}
            renderInput={(params) => (
                <TextField {...params} label="Add question" />
            )}
        />
    );
};

const QuestionsSection: React.FC<QuestionsSectionProps> = ({
    questions,
    validTargetQuestions,
    allQuestions = [],
    onDragEnd,
    collapsedSections,
    onToggleSection,
    onRemoveQuestion,
    onAddTrigger,
    onAddShowWhen,
    onRemoveTrigger,
    onTriggerAnswersChange,
    onTriggerTargetChange,
    onTriggerActionChange,
    onTriggerToAnswersChange,
    onConditionClauseChange,
    onAddConditionClause,
    onRemoveConditionClause,
    onOperatorChange,
    questionsAvailableToAdd = [],
    onAddQuestion,
    showAddQuestion = true,
    droppableId,
    onTriggerDragEnd,
    onExpressionChange,
    onSpanScopeChange,
    globalQuestionPool = [],
    crossWorkflowTargetQuestions = [],
    hostCodeType,
    crossWorkflowSourceQuestions,
}) => {
    const spanScopeSourceQuestions = React.useMemo(
        () => [
            ...(crossWorkflowSourceQuestions?.codeWorkflows ?? []),
            ...(crossWorkflowSourceQuestions?.caseWorkflows ?? []),
        ].filter((wq) => wq.question.questionType === QuestionType.SPANS),
        [crossWorkflowSourceQuestions],
    );

    const showWhenSection = React.useMemo(
        () => ({
            title: "Show when",
            addLabel: "Add Show when",
            emptyText: "No visibility rules yet. Add one to make this question conditional.",
            phase: "PRE" as const,
            onAdd: onAddShowWhen,
            hideActionControls: true,
            droppablePrefix: "show-when",
        }),
        [onAddShowWhen],
    );
    const triggersSection = React.useMemo(
        () => ({
            title: "Triggers",
            addLabel: "Add Trigger",
            emptyText: "No triggers yet. Add one to define the workflow logic.",
            phase: "POST" as const,
            onAdd: onAddTrigger,
            droppablePrefix: "triggers",
        }),
        [onAddTrigger],
    );

    const getAnswersForQuestion = React.useCallback((questionId: number, selectedAnswerIds: number[] = []): AnswerValue[] => {
        if (questionId === SPAN_ANSWERED_ID || questionId === SPAN_NOT_ANSWERED_ID) {
            return [
                { id: SPAN_ANSWERED_ID, name: "span_answered", displayText: "Span is selected", active: true } as AnswerValue,
                { id: SPAN_NOT_ANSWERED_ID, name: "span_not_answered", displayText: "No span selected", active: true } as AnswerValue,
            ];
        }
        const wq =
            allQuestions.find((q) => q.question.id === questionId) ??
            (crossWorkflowSourceQuestions?.codeWorkflows ?? []).find((q) => q.question.id === questionId) ??
            (crossWorkflowSourceQuestions?.caseWorkflows ?? []).find((q) => q.question.id === questionId);
        if (!wq) return [];
        if (wq.question.questionType === "SYSTEM") {
            return [
                { id: -1, name: "true", displayText: "Expression is true", active: true } as AnswerValue,
                { id: -2, name: "false", displayText: "Expression is false", active: true } as AnswerValue,
            ];
        }
        if (isSpanQuestionType(wq.question.questionType)) {
            return [
                { id: SPAN_ANSWERED_ID, name: "span_answered", displayText: "Span is selected", active: true } as AnswerValue,
                { id: SPAN_NOT_ANSWERED_ID, name: "span_not_answered", displayText: "No span selected", active: true } as AnswerValue,
            ];
        }
        return getAnswersIncludingSelected(wq.question, selectedAnswerIds);
    }, [allQuestions, crossWorkflowSourceQuestions]);

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <DragDropContext onDragEnd={(result: DropResult) => {
                    if (result.type === 'TRIGGER') {
                        if (result.destination && onTriggerDragEnd) {
                            if (result.destination.droppableId !== result.source.droppableId) {
                                return;
                            }
                            const droppablePrefix = result.source.droppableId.startsWith("show-when-")
                                ? "show-when-"
                                : "triggers-";
                            const questionClientId = result.source.droppableId.replace(droppablePrefix, "");
                            const workflowQuestion = questions.find((q) => q.clientId === questionClientId);
                            const phase: WorkflowTriggerPhase = droppablePrefix === "show-when-" ? "PRE" : "POST";
                            const triggerEntries = (workflowQuestion?.triggers ?? [])
                                .map((trigger, triggerIndex) => ({ trigger, triggerIndex }))
                                .filter(({ trigger }) =>
                                    phase === "PRE"
                                        ? isShowWhenTrigger(trigger)
                                        : getTriggerPhase(trigger) === "POST" && !isShowWhenTrigger(trigger)
                                );
                            const sourceIndex = triggerEntries[result.source.index]?.triggerIndex;
                            if (sourceIndex == null) {
                                return;
                            }
                            const remainingEntries = triggerEntries.filter((entry) => entry.triggerIndex !== sourceIndex);
                            const destinationEntry = remainingEntries[result.destination.index];
                            const destinationIndex = destinationEntry
                                ? destinationEntry.triggerIndex > sourceIndex
                                    ? destinationEntry.triggerIndex - 1
                                    : destinationEntry.triggerIndex
                                : remainingEntries.length > 0
                                    ? (remainingEntries[remainingEntries.length - 1].triggerIndex > sourceIndex
                                        ? remainingEntries[remainingEntries.length - 1].triggerIndex - 1
                                        : remainingEntries[remainingEntries.length - 1].triggerIndex) + 1
                                    : sourceIndex;
                            onTriggerDragEnd(questionClientId, sourceIndex, destinationIndex);
                        }
                    } else {
                        onDragEnd(result);
                    }
                }}>
                <StrictModeDroppable droppableId={droppableId} type="QUESTION">
                    {(droppableProvided) => (
                        // deepcode ignore DomXss: False positive - react-beautiful-dnd droppableProps are safe library props
                        <Stack spacing={2} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {questions.map((workflowQuestion, index) => {
                                
                                
                                const question = workflowQuestion?.question;
                                if (!question) {
                                    return null;
                                }
                                const questionKey =
                                    workflowQuestion.clientId ?? String(question.id ?? index);
                                const isCollapsed = collapsedSections[questionKey] ?? false;
                                const draggableId = workflowQuestion.clientId ?? `workflow-question-${questionKey}`;

                                return (
                                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                                        {(draggableProvided, snapshot) => (
                                            <Paper
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: questionCardPalette.background,
                                                    borderColor: questionCardPalette.border,
                                                    borderWidth: 1,
                                                    borderStyle: "solid",
                                                    flexDirection: "column",
                                                    opacity: snapshot.isDragging ? 0.98 : 1,
                                                    transition: "opacity 0.2s ease",
                                                    boxShadow: "none",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ py: 1.5, px: 2 }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Box
                                                            component="span"
                                                            {...draggableProvided.dragHandleProps}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                cursor: "grab",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            <DragIndicator fontSize="small" />
                                                        </Box>
                                                        <Typography variant="subtitle1">
                                                            {question.name}
                                                        </Typography>
                                                        <Chip
                                                            label={question.questionType}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title={isCollapsed ? "Expand section" : "Collapse section"}>
                                                            <IconButton onClick={() => onToggleSection(questionKey)}>
                                                                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Remove question">
                                                            <IconButton onClick={() => onRemoveQuestion(workflowQuestion.clientId)}>
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                                                    <Divider sx={{ mx: 2 }} />
                                                    <Box sx={{ backgroundColor: questionCardPalette.background, p: 2 }}>
                                                        {question.questionText ? (
                                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                                {question.questionText}
                                                            </Typography>
                                                        ) : null}
                                {question.questionType === 'SYSTEM' && (
                                                            <Box sx={{ mb: 2 }}>
                                                                <ExpressionBuilder
                                                                    expression={workflowQuestion.question.expression ?? ''}
                                                                    onChange={(expr) => onExpressionChange?.(workflowQuestion.clientId, expr)}
                                                                    disabled
                                                                />
                                                            </Box>
                                                        )}
                                                        {hostCodeType === "SPAN" && (
                                                            <Stack
                                                                direction={{ xs: "column", md: "row" }}
                                                                spacing={1}
                                                                sx={{ mb: 2 }}
                                                            >
                                                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                                                    <InputLabel id={`span-scope-${questionKey}`}>Applies to</InputLabel>
                                                                    <Select
                                                                        labelId={`span-scope-${questionKey}`}
                                                                        label="Applies to"
                                                                        value={workflowQuestion.spanScope ?? "ALL_SPANS"}
                                                                        onChange={(event) => {
                                                                            const nextScope = event.target.value as SpanWorkflowQuestionScope;
                                                                            onSpanScopeChange?.(
                                                                                workflowQuestion.clientId,
                                                                                nextScope,
                                                                                nextScope === "SELECTED_BY_SPANS_QUESTION"
                                                                                    ? workflowQuestion.spanScopeQuestionId ?? null
                                                                                    : null,
                                                                            );
                                                                        }}
                                                                    >
                                                                        {spanScopeOptions.map((option) => (
                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                                {workflowQuestion.spanScope === "SELECTED_BY_SPANS_QUESTION" && (
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel id={`span-scope-source-${questionKey}`}>Source SPANS question</InputLabel>
                                                                        <Select
                                                                            labelId={`span-scope-source-${questionKey}`}
                                                                            label="Source SPANS question"
                                                                            value={workflowQuestion.spanScopeQuestionId ?? ""}
                                                                            onChange={(event) => {
                                                                                const rawValue = event.target.value;
                                                                                onSpanScopeChange?.(
                                                                                    workflowQuestion.clientId,
                                                                                    "SELECTED_BY_SPANS_QUESTION",
                                                                                    rawValue === "" ? null : Number(rawValue),
                                                                                );
                                                                            }}
                                                                        >
                                                                            {spanScopeSourceQuestions.map((source) => (
                                                                                <MenuItem key={source.question.id} value={source.question.id}>
                                                                                    {source.question.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                        {spanScopeSourceQuestions.length === 0 && (
                                                                            <FormHelperText>
                                                                                Add a SPANS question to a case or code workflow to scope this span question.
                                                                            </FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                )}
                                                            </Stack>
                                                        )}
                                                        {hostCodeType === "SPAN" && (
                                                            <TriggerSection
                                                                workflowQuestion={workflowQuestion}
                                                                section={showWhenSection}
                                                                questions={questions}
                                                                validTargetQuestions={validTargetQuestions}
                                                                allQuestions={allQuestions}
                                                                globalQuestionPool={globalQuestionPool}
                                                                crossWorkflowTargetQuestions={crossWorkflowTargetQuestions}
                                                                crossWorkflowSourceQuestions={crossWorkflowSourceQuestions}
                                                                hostCodeType={hostCodeType}
                                                                onRemoveTrigger={onRemoveTrigger}
                                                                onTriggerAnswersChange={onTriggerAnswersChange}
                                                                onTriggerTargetChange={onTriggerTargetChange}
                                                                onTriggerActionChange={onTriggerActionChange}
                                                                onTriggerToAnswersChange={onTriggerToAnswersChange}
                                                                onConditionClauseChange={onConditionClauseChange}
                                                                onAddConditionClause={onAddConditionClause}
                                                                onRemoveConditionClause={onRemoveConditionClause}
                                                                onOperatorChange={onOperatorChange}
                                                                getAnswersForQuestion={getAnswersForQuestion}
                                                            />
                                                        )}
                                                        <TriggerSection
                                                            workflowQuestion={workflowQuestion}
                                                            section={triggersSection}
                                                            questions={questions}
                                                            validTargetQuestions={validTargetQuestions}
                                                            allQuestions={allQuestions}
                                                            globalQuestionPool={globalQuestionPool}
                                                            crossWorkflowTargetQuestions={crossWorkflowTargetQuestions}
                                                            crossWorkflowSourceQuestions={crossWorkflowSourceQuestions}
                                                            hostCodeType={hostCodeType}
                                                            onRemoveTrigger={onRemoveTrigger}
                                                            onTriggerAnswersChange={onTriggerAnswersChange}
                                                            onTriggerTargetChange={onTriggerTargetChange}
                                                            onTriggerActionChange={onTriggerActionChange}
                                                            onTriggerToAnswersChange={onTriggerToAnswersChange}
                                                            onConditionClauseChange={onConditionClauseChange}
                                                            onAddConditionClause={onAddConditionClause}
                                                            onRemoveConditionClause={onRemoveConditionClause}
                                                            onOperatorChange={onOperatorChange}
                                                            getAnswersForQuestion={getAnswersForQuestion}
                                                        />
                                                    </Box>
                                                </Collapse>
                                            </Paper>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder as any}
                        </Stack>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            {showAddQuestion && questionsAvailableToAdd.length > 0 && (
                <>
                    <Divider />
                    <AddQuestionAutocomplete
                        options={questionsAvailableToAdd}
                        onAddQuestion={onAddQuestion}
                    />
                </>
            )}
        </Stack>
    );
};

export default QuestionsSection;
